import { defineModule, } from 'direct-vuex';
import { AddressData, } from '~/utils/models';
import { isDate, isObject, isValidNumber, } from '~/utils/models/isObject';
import { sessionStorageValue, } from '~/utils/store';

interface UserMeterDetails {
	id: string;
	category: string;
	lastReading: number;
	lastReadDate: Date;
}

export interface MeterDetails {
	contractId: string;
	propertyAddress: AddressData;
	meter: UserMeterDetails;
}

export interface MeterReadState {
	currentStep: 1 | 2 | null;
	meterDetails: MeterDetails[] | null;
	currentMeterReading: number | null;
	selectedMeterDetails: null | MeterDetails;
	isSubmitMeterReadForcefullySucceeded: null | boolean;
}

const MeterDetailsStorage = sessionStorageValue('meterReading.meterDetails', isMeterDetails,
	() => null,
	JSON.stringify,
	(v) => {
		const userInput = JSON.parse(v) as MeterDetails;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		userInput.meter.lastReadDate = new Date(userInput.meter.lastReadDate);
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return userInput;
	},
);

const MeterReadStorage = sessionStorageValue('meterReading.meterRead', isValidNumber,
	() => null,
	(v) => v.toString(10),
	(v) => parseInt(v, 10),
);

function isMeterDetails(input: unknown): input is MeterDetails {
	return (
		true &&
		isObject(input) &&
		typeof input.id === 'string' &&
		typeof input.category === 'string' &&
		typeof input.contractId === 'string' &&
		typeof input.lastReading === 'number' &&
		isDate(input.lastReadDate) &&
		true
	);
}

function initialiseUserMeterDetails() {
	return MeterDetailsStorage.get();
}

function initialiseMeterRead() {
	return MeterReadStorage.get();
}

export default defineModule({
	namespaced: true as const,
	state: {
		currentStep: 1,
		meterDetails: initialiseUserMeterDetails(),
		currentMeterReading: initialiseMeterRead(),
		selectedMeterDetails: null as (null | MeterDetails),
		isSubmitMeterReadForcefullySucceeded: null as (null | boolean),
	} as MeterReadState,
	getters: {
		hasExistingMeterDetails(state) {
			return state.meterDetails !== null;
		},
	},
	mutations: {
		setStep(state, step: MeterReadState['currentStep']) {
			state.currentStep = step;
		},
		updateUserMeterDetails(state, meterDetails) {
			MeterDetailsStorage.set(meterDetails);
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			state.meterDetails = meterDetails;
		},
		setMeterReading(state, meterReading: number) {
			state.currentMeterReading = meterReading;
			MeterReadStorage.set(meterReading);
		},
		setLatestMeterReading(state, meterReading: number) {
			if (state.meterDetails) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				state.selectedMeterDetails!.meter.lastReading = meterReading;
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				state.selectedMeterDetails!.meter.lastReadDate = new Date();
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				MeterDetailsStorage.set(state.selectedMeterDetails!);
			}
		},
		setSelectedMeterDetails(state, selectedMeterDetails: MeterDetails) {
			state.selectedMeterDetails = selectedMeterDetails;
		},
		setSubmitMeterReadForcefullySucceeded(state, boolVal:boolean) {
			state.isSubmitMeterReadForcefullySucceeded = boolVal;
		},
		reset(state) {
			state.currentMeterReading = null;
			state.currentStep = null;
			state.meterDetails = null;
			MeterReadStorage.remove();
			MeterDetailsStorage.remove();
			state.selectedMeterDetails = null;
			state.isSubmitMeterReadForcefullySucceeded = null;
		},
	},
});
